import { IncompleteActivityState } from 'src/app/modules/activity-save-state/interfaces/incomplete-activity-state';
import { Injectable } from '@angular/core';
import { ClickstreamService } from 'src/app/modules/olp-api/services/clickstream/clickstream.service';
import {
  Activity,
  Population,
  PopulationConstructedResponse,
  PopulationDragAndDrop,
  PopulationInlineChoice,
  PopulationTextEntry,
  PopulationDNDGrid,
  PopulationHotText,
} from '../../interfaces';
import { QuestionLogService } from 'src/app/modules/olp-api/services/question-log/question-log.service';
import {
  ConstructedResponseTypes,
  InlineChoiceTypes,
  SkipQuestionLogSortTypes,
  TemplateID,
} from '../../enums';
import { ResultsService } from '../results/results.service';
import {
  ClickstreamEventTypeName,
  ClickstreamInteractionEventType,
} from 'src/app/modules/olp-api/enums/clickstream-events';
import { PopulationService } from '../population/population.service';
import { ClickstreamInteraction } from 'src/app/modules/olp-api/types/clickstream-interaction.interface';
import { QuestionState } from 'src/app/modules/olp-api/enums/question-log';
import { TimeElapsedService } from '../time-elapsed/time-elapsed.service';
import { FeatureFlags } from '../../enums/feature-flags';
import { FeatureFlagService } from 'src/app/modules/feature-flags/services/feature-flag/feature-flag.service';

@Injectable({
  providedIn: 'root',
})
export class ConstructClickstreamService {
  constructor(
    private clickstreamService: ClickstreamService,
    private questionLogService: QuestionLogService,
    private resultsService: ResultsService,
    private populationService: PopulationService,
    private timeElapsedService: TimeElapsedService,
    private featureFlagService: FeatureFlagService
  ) {}

  generateClickstream(
    activityState: IncompleteActivityState,
    activityData: Activity,
    contentOid: number
  ): void {
    if (this.featureFlagService.isFlagEnabled(FeatureFlags.USE_CLICKSTREAM)) {
      // Create a clickstream session
      this.clickstreamService.establishSession();

      // supervisor context
      this.clickstreamService.setSupervisorContext(
        this.getSupervisorContextDataFromSubject(activityData.product)
      );

      // activity begin
      this.clickstreamService.startActivity(
        activityData.id, // uuid from airtable
        contentOid,
        activityData.episodeTitle ?? 'unknown generated clickstream activity'
      );

      // add the AmiraActivityContinue event for Student Inbox activities
      if (
        this.featureFlagService.isFlagEnabled(
          FeatureFlags.ENABLE_STUDENT_INBOX
        ) &&
        activityData.inboxActivityId
      ) {
        this.clickstreamService.continueAmiraActivity(
          activityData.inboxActivityId
        );
      }

      this.parseInteractionsIntoEvents(activityState, activityData);

      // ActivityComplete
      this.clickstreamService.completeActivity(
        this.resultsService.getPercentage(),
        this.resultsService.earnedStars,
        this.timeElapsedService.getTimeAsSeconds()
      );

      if (
        this.featureFlagService.isFlagEnabled(FeatureFlags.ENABLE_STUDENT_INBOX)
      ) {
        this.clickstreamService.completeAmiraActivity(
          this.resultsService.getPercentage(),
          this.timeElapsedService.getTimeAsSeconds(),
          activityData.inboxActivityId
        );
      }

      this.clickstreamService.saveClickstreamData();
      this.clickstreamService.clearCache();
    }
  }

  // eslint-disable-next-line complexity
  parseInteractionsIntoEvents(
    activityState: IncompleteActivityState,
    activityData: Activity
  ): void {
    const clickstreamInteractions =
      this.clickstreamService.getClickstreamInteractions();
    let pop: Population | undefined;
    let attempt = 0;
    let correctAnswers: string[] | string[][] = [];
    // eslint-disable-next-line complexity
    clickstreamInteractions.forEach(interact => {
      let p;
      switch (interact.type) {
        case ClickstreamInteractionEventType.EntrySurvey:
          this.handleSurveyInteraction(interact);
          break;
        case ClickstreamInteractionEventType.Video:
          this.handleVideoInteraction(interact);
          break;
        case ClickstreamInteractionEventType.AskQuestion:
          p = this.handleAskQuestion(interact, activityData);
          if (p) {
            pop = p;
            attempt = 0;
            correctAnswers = this.getCorrectAnswers(pop);
          }
          break;
        case ClickstreamInteractionEventType.ReAskQuestion:
          if (pop) {
            attempt += 1;
            this.handleReAskQuestion(interact, pop, attempt);
          }
          break;
        case ClickstreamInteractionEventType.AnswerQuestion:
          if (pop) {
            const fin = this.handleAnswer(
              interact,
              pop,
              attempt,
              correctAnswers,
              activityState,
              activityData
            );
            if (fin) {
              pop = undefined;
              attempt = 0;
              correctAnswers = [];
            }
          }
          break;
        case ClickstreamInteractionEventType.Inactivity:
          this.recordInactivity(interact);
          break;
        default:
          break;
      }
    });
  }

  handleSurveyInteraction(interact: ClickstreamInteraction): void {
    if (interact.manifestId === 'Survey skipped') {
      this.questionLogService.recordSkippedSelfRating(interact.offset);
    } else {
      const promptRes = interact.data as {
        promptText: string;
        response: string;
        timeElapsed: number;
      };
      this.questionLogService.recordSelfRating(
        promptRes.promptText,
        promptRes.response,
        interact.offset,
        promptRes.timeElapsed
      );
    }
  }

  handleVideoInteraction(interact: ClickstreamInteraction): void {
    const promptRes = interact.data as {
      timeWatched: number;
      videoDuration: number;
    };
    this.clickstreamService.addEventFromProperties(
      ClickstreamEventTypeName.Event,
      'Video',
      {
        timeWatched: promptRes.timeWatched,
        videoDuration: promptRes.videoDuration,
        videoId: interact.manifestId,
      },
      interact.offset
    );
  }

  handleAskQuestion(
    interact: ClickstreamInteraction,
    activityData: Activity
  ): Population | null {
    const popIndex = activityData.populations.findIndex(
      pop => pop.id === interact.manifestId
    );
    if (popIndex < 0) {
      console.warn(
        `trying to generate question event for ${interact.manifestId}, but it wasn't found.`
      );
      return null;
    }

    const pop = activityData.populations[popIndex];

    this.askQuestion(pop, 0, interact.offset);

    return pop;
  }

  handleReAskQuestion(
    interact: ClickstreamInteraction,
    pop: Population,
    attempt: number
  ): void {
    this.askQuestion(pop, attempt, interact.offset);
  }

  handleAnswer(
    interact: ClickstreamInteraction,
    pop: Population,
    attempt: number,
    correctAnswers: string[] | string[][],
    activityState: IncompleteActivityState,
    activityData: Activity
  ): boolean {
    const popIndex = activityData.populations.findIndex(p => p.id === pop.id);

    if (popIndex !== -1) {
      return this.answerQuestion(
        [activityState.populationResponses[popIndex].responses[attempt]],
        pop,
        correctAnswers,
        attempt,
        interact.offset
      );
    }
    return true;
  }

  private getCorrectAnswers(pop: Population): string[] | string[][] {
    if (ConstructedResponseTypes.includes(pop.templateID)) {
      return [];
    } else if (pop.templateID === TemplateID.TEXT_ENTRY) {
      const popData = pop.populationData as PopulationTextEntry;
      return popData.answers[0].map(answer => answer.toString());
    } else if (
      pop.templateID === TemplateID.SORTING_BUCKETS ||
      pop.templateID === TemplateID.SORTING_BUCKETS_IMAGES
    ) {
      const popData = pop.populationData as PopulationDragAndDrop;
      return popData.answers as string[][];
    } else {
      const { correctAnswers } = this.populationService.getAnswerArrays(pop);
      return correctAnswers;
    }
  }

  // eslint-disable-next-line complexity
  private askQuestion(pop: Population, attempt: number, offset: number): void {
    let question = InlineChoiceTypes.includes(pop.templateID)
      ? this.buildInlineQuestion(pop)
      : pop.populationData.question?.text ?? '';

    if (question.trim().length === 0) {
      question = pop.instructions?.text ?? '';
    }

    if (ConstructedResponseTypes.includes(pop.templateID)) {
      const popData = pop.populationData as PopulationConstructedResponse;
      this.questionLogService.askOpenEndedQuestion(
        pop.id,
        question,
        pop.templateID,
        offset,
        popData.chart?.data,
        popData.options.characterLimit,
        pop.passage,
        pop.image
      );
    } else if (pop.templateID === TemplateID.TEXT_ENTRY) {
      const popData = pop.populationData as PopulationTextEntry;
      this.questionLogService.askTextEntryQuestion(
        pop.id,
        question,
        popData.answers[0].map(answer => answer.toString()),
        pop.templateID,
        offset,
        pop.passage,
        pop.image
      );
    } else if (
      pop.templateID === TemplateID.SORTING_BUCKETS ||
      pop.templateID === TemplateID.SORTING_BUCKETS_IMAGES
    ) {
      const popData = pop.populationData as PopulationDragAndDrop;
      this.questionLogService.askDndBucketQuestion(
        pop.id,
        question,
        popData.labels?.map(label => label.text) ?? [],
        popData.answers as string[][],
        popData.answerChoices?.map(
          choice => choice.choice.texts?.at(0)?.text ?? ''
        ) ?? [],
        pop.templateID,
        offset,
        pop.passage,
        pop.image
      );
    } else if (
      pop.templateID === TemplateID.DND_GRID ||
      pop.templateID === TemplateID.DND_GRID_MATCH
    ) {
      if (attempt === 0) {
        // only log question on first attempt
        const popData = pop.populationData as PopulationDNDGrid;
        this.questionLogService.askDndGridQuestion(
          pop.id,
          question,
          popData.numCorrectAnswers ?? 0,
          pop.templateID,
          offset,
          pop.standard?.text ?? ''
        );
      }
    } else if (pop.templateID === TemplateID.HOT_TEXT) {
      if (attempt === 0) {
        // only log question on first attempt
        const popData = pop.populationData as PopulationHotText;
        this.questionLogService.askHotTextQuestion(
          pop.id,
          question,
          popData.answers[0].map(answer => answer.toString()),
          false,
          pop.templateID,
          QuestionState.initial,
          offset,
          popData.text[0],
          popData.wordsSplit as string[]
        );
      }
    } else {
      const { correctAnswers, answerChoices } =
        this.populationService.getAnswerArrays(pop);

      if (attempt <= 0) {
        this.questionLogService.askQuestion(
          pop.id,
          question,
          correctAnswers,
          answerChoices,
          false,
          pop.templateID,
          !SkipQuestionLogSortTypes.includes(pop.templateID),
          QuestionState.initial,
          offset,
          pop.passage,
          pop.image
        );
      } else {
        this.questionLogService.reAskQuestion(
          pop.id,
          question,
          correctAnswers,
          answerChoices,
          true,
          pop.templateID,
          !SkipQuestionLogSortTypes.includes(pop.templateID),
          QuestionState.reAskQuestion,
          offset,
          pop.passage,
          pop.image
        );
      }
    }
  }

  /**
   * @returns boolean true if question is fully answered and false if not fully answered
   */
  // eslint-disable-next-line complexity
  private answerQuestion(
    responses: (string | number)[][][],
    pop: Population,
    correctAnswers: string[] | string[][],
    attempt: number,
    offset: number
  ): boolean {
    const onLastAttempt = attempt >= (pop.populationData.tries ?? 2) - 1;

    if (ConstructedResponseTypes.includes(pop.templateID)) {
      this.questionLogService.answerOpenEndedQuestion(
        pop.id ?? '',
        (responses as string[][][])[0][0][0],
        offset
      );
      // open ended questions only get answered once
      return true;
    } else if (pop.templateID === TemplateID.TEXT_ENTRY) {
      return (
        this.questionLogService.answerTextEntryQuestion(
          pop.id ?? '',
          (responses as string[][][])[0][0],
          correctAnswers as string[],
          offset
        ) || onLastAttempt
      );
    } else if (
      pop.templateID === TemplateID.SORTING_BUCKETS ||
      pop.templateID === TemplateID.SORTING_BUCKETS_IMAGES
    ) {
      return (
        this.questionLogService.answerDndBucketQuestion(
          pop,
          (responses as string[][][])[0],
          offset
        ) || onLastAttempt
      );
    } else if (
      pop.templateID === TemplateID.DND_GRID ||
      pop.templateID === TemplateID.DND_GRID_MATCH
    ) {
      return (
        this.questionLogService.answerDndGridQuestion(
          pop,
          (responses as string[][][])[0][0],
          attempt,
          offset
        ) || onLastAttempt
      );
    } else if (pop.templateID === TemplateID.HOT_TEXT) {
      return (
        this.questionLogService.answerHotTextQuestion(
          pop.populationData as PopulationHotText,
          pop.id ?? '',
          (responses as string[][][])[0][0],
          offset
        ) || onLastAttempt
      );
    } else {
      const sortResponse = !SkipQuestionLogSortTypes.includes(
        this.populationService.getCurrentPopulationType()
      );

      const res = (responses as string[][][])[0][0];
      const questionState =
        attempt <= 0 ? QuestionState.askQuestion : QuestionState.reAskQuestion;
      return (
        this.questionLogService.answerQuestion(
          pop,
          res,
          sortResponse,
          questionState,
          offset
        ) || onLastAttempt
      );
    }
  }

  private buildInlineQuestion(population: Population): string {
    if (
      population.templateID !== TemplateID.INLINE_CHOICE &&
      population.templateID !== TemplateID.TEXT_ENTRY &&
      population.templateID !== TemplateID.FILL_IN_THE_BLANK_TILE_TEXT
    ) {
      console.warn(
        'Attempted to build inline choice question from non-inline choice question',
        population.templateID
      );
      return '';
    }

    const popData = population.populationData as PopulationInlineChoice;
    let question = '';
    popData.text.forEach(text => {
      const sanitizedQuestionText = text.text
        .replace(/\{WORD_TOKEN\}/g, '______') // remove all blank words
        .replace(/\{CHOICE_TOKEN\}/g, '______') // remove all blank choices
        .replace(/\{NUMBER_TOKEN\}/g, '______') // remove all blank numbers
        .replace(/\{DND_TOKEN\}/g, '______'); // remove all blank drag and drop tokens
      question += sanitizedQuestionText;
    });

    return question;
  }

  private getSupervisorContextDataFromSubject(subject?: string): string {
    switch (subject) {
      case 'Lectura':
        return 'Microlesson_SpanishReading';
      case 'Reading':
        return 'Microlesson_Reading';
      case 'Math':
        return 'Microlesson_Math';
      case 'Mattematicas':
        return 'Microlesson_Matematicas';
      default:
        console.warn(
          'unknown subject in getSupervisorContextDataFromSubject:',
          subject
        );
        return 'Microlesson_Reading';
    }
  }

  private recordInactivity(interaction: ClickstreamInteraction): void {
    this.clickstreamService.addEventFromProperties(
      ClickstreamEventTypeName.Inactivity,
      'Inactivity',
      (interaction.data as { inactivityTime: number }).inactivityTime,
      interaction.offset
    );
  }
}
